<template>
  <header :class="['guest-header', { 'guest-header--static': !fixed }]">
    <div class="guest-header__container">
      <router-link v-if="showLogo" to="/" class="guest-header__logo-block">
        <img src="@/assets/logo-alfabit.svg" alt="AlfaBit" class="logo-icon-img theme-logo" />
        <span class="logo-texts">
          <span class="logo-subtitle">Payments</span>
        </span>
      </router-link>
      <div class="guest-header__right">
        <div class="guest-header__a11y">
          <AccessibilityMenu />
        </div>
        <div class="guest-header__theme">
          <ThemeSwitcher />
        </div>
        <div class="guest-header__lang">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import ThemeSwitcher from '@/components/ThemeSwitcher.vue';
import AccessibilityMenu from '@/components/AccessibilityMenu.vue';
import { useI18n } from '@/composables/useI18n';
const props = defineProps({
  fixed: { type: Boolean, default: true },
  showLogo: { type: Boolean, default: true }
});
const route = useRoute();
const { t } = useI18n();
const isAuthPage = computed(() => route.path === '/login' || route.path === '/register');
</script>

<style scoped>
.guest-header {
  width: 100%;
  background: rgba(11, 14, 17, 0.9);
  border-bottom: 1px solid var(--border-primary);
  padding: 0.5rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(16px);
}

:global([data-theme="light"]) .guest-header {
  background: rgba(255, 255, 255, 0.9) !important;
  border-bottom: 1px solid var(--border-primary) !important;
  box-shadow: none !important;
}
.guest-header--static {
  position: static !important;
  top: unset;
}
.guest-header__container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  min-width: 0;
}
.guest-header__logo-block {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  text-decoration: none;
  min-width: 0;
  flex: 1 1 auto;
}
.guest-header__right {
  display: flex;
  align-items: center;
  margin-left: auto;
  gap: 0.7rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}
.guest-header__nav {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}
.guest-header__btn {
  padding: 0.5rem 1.1rem;
  border-radius: 0.8rem;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  color: var(--primary-blue);
  background: transparent;
  border: 1px solid var(--primary-blue);
  transition: background 0.2s, color 0.2s;
  display: flex;
  align-items: center;
  height: 2.4rem;
}
.guest-header__btn.btn-primary {
  background: var(--primary-blue);
  color: #fff;
  border: 1px solid var(--primary-blue);
}
.guest-header__btn.btn-secondary {
  background: transparent;
  color: var(--primary-blue);
  border: 1px solid var(--primary-blue);
}
.guest-header__btn:hover, .guest-header__btn.btn-primary:hover {
  background: var(--primary-blue-hover);
  color: #fff;
}
.guest-header__theme {
  display: flex;
  align-items: center;
  height: 2.4rem;
}
.guest-header__theme :deep(.theme-switcher) {
  height: 2.4rem;
  width: 2.4rem;
  display: flex;
  align-items: center;
}
.guest-header__a11y {
  display: flex;
  align-items: center;
  height: 2.4rem;
}
.guest-header__a11y :deep(.a11y-trigger) {
  height: 2.4rem;
  width: 2.4rem;
}
.guest-header__lang {
  display: flex;
  align-items: center;
  height: 2.4rem;
}
.guest-header__lang :deep(.language-switcher) {
  height: 2.4rem;
  display: flex;
  align-items: center;
}
@media (max-width: 600px) {
  .guest-header__container {
    padding: 0 0.5rem;
    flex-wrap: wrap;
  }
  .guest-header__logo-block {
    margin-right: 0;
    flex-shrink: 1;
    min-width: 0;
  }
  .logo-icon-img {
    width: 86px;
    height: auto;
  }
  .logo-subtitle {
    font-size: 0.7rem;
  }
  .guest-header__btn {
    font-size: 0.8rem;
    padding: 0.3rem 0.5rem;
    height: 1.9rem;
    white-space: nowrap;
  }
  .guest-header__lang,
  .guest-header__theme,
  .guest-header__a11y {
    height: 1.9rem;
  }
  .guest-header__theme :deep(.theme-switcher) {
    height: 1.9rem;
    width: 1.9rem;
  }
  .guest-header__a11y :deep(.a11y-trigger) {
    height: 1.9rem;
    width: 1.9rem;
  }
  .guest-header__right {
    gap: 0.2rem;
    flex-shrink: 0;
  }
  .guest-header__nav {
    gap: 0.3rem;
  }
}

/* Large a11y text: keep controls usable, allow logo text to shrink */
:global(html[data-a11y-font-scale="125"]) .logo-title,
:global(html[data-a11y-font-scale="150"]) .logo-title {
  font-size: 1.1rem;
}
:global(html[data-a11y-font-scale="150"]) .logo-subtitle {
  font-size: 0.8rem;
}
.logo-icon-img {
  width: 96px;
  height: auto;
  object-fit: contain;
}
.logo-texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;
}
.logo-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--text-primary);
  background: none;
  -webkit-text-fill-color: currentColor;
  margin: 0;
  letter-spacing: -0.03em;
}
.logo-subtitle {
  font-size: 0.95rem;
  color: var(--text-tertiary);
  font-weight: 500;
  margin: 0;
}
</style> 